// @ts-nocheck
/* eslint-disable react-hooks/rules-of-hooks */
import { useSafeSetState } from 'hooks';
import { useRouterListContext } from 'components/grid/api-list/hooks/list-router-context';
import {
  sanitizeFilters,
  getInitialSearch,
  getListStateKeys,
} from 'components/grid/api-list/hooks/list-context/utils';
import { get, invert, pick } from 'lodash';
import { set } from 'lodash/fp';

export const SORT_MAP = {
  ASC: 'ASC',
  DESC: 'DESC',
};

export const SORT_SIGN = {
  [SORT_MAP.ASC]: '+',
  [SORT_MAP.DESC]: '-',
};

export const useStateListContext = (props = {}) => {
  const [state, setListState] = useSafeSetState(getListStateFromProps(props));
  // We set sortMap for easier manipulation of sort...but sort is eventually represented as string with sort fields separated by comma
  const sortMap = new Map(sortFromString(state.sort));

  function getListStateFromProps() {
    const {
      sort = '',
      filter = {},
      page = 1,
      perPage = 10,
      search = '',
    } = props;

    return {
      sort,
      filter,
      page,
      perPage,
      search, //getInitialSearch(props),
    };
  }

  function setSort(sortKey) {
    const currentSortValue = getSortValue(sortKey);
    // Sort cycle goes from ASC, DESC to unset...we check each state
    if (currentSortValue === SORT_MAP.DESC) {
      if (sortMap.has(sortKey)) {
        sortMap.delete(sortKey);
      }
    } else if (currentSortValue === SORT_MAP.ASC) {
      sortMap.set(sortKey, SORT_MAP.DESC);
    } else {
      sortMap.set(sortKey, SORT_MAP.ASC);
    }

    setListState({ page: 1, sort: sortToString([...sortMap]) });
  }

  function isSortSet(fieldName) {
    const sortValue = getSortValue(fieldName);
    return Object.values(SORT_MAP).includes(sortValue);
  }

  function getSortValue(fieldName) {
    return sortMap.get(fieldName);
  }

  function sortToString(sort = state.sort) {
    return sort.map(([name, value]) => `${SORT_SIGN[value]}${name}`).join(',');
  }

  function sortFromString(sort = '') {
    function parseSortString(sortString) {
      const sortSign = sortString.substring(0, 1);
      const name = sortString.substring(1);

      return [name, get(invert(SORT_SIGN), sortSign)];
    }

    if (sort) {
      return sort.split(',').map(parseSortString);
    }

    return [];
  }

  const context = {
    ...state,
    getListState() {
      return pick(state, getListStateKeys());
    },
    getListStateKeys,
    setSort,
    isSortSet,
    getSortValue,
    setFilterKey(name, value) {
      setListState((state) => {
        if (name === 'search') {
          return {
            ...state,
            ...set(name, value, state),
            page: 1,
          };
        }

        const newFilter = { ...state.filter, [name]: value };

        if (!value) {
          delete newFilter[name];
        }

        return {
          ...state,
          filter: sanitizeFilters(newFilter),
          page: 1,
        };
      });
    },
    setFilter({ search = '' /*getInitialSearch(props)*/, ...filter } = {}) {
      setListState({
        filter: sanitizeFilters(filter),
        search,
        page: 1,
      });
    },
    setPage(page) {
      setListState({ page });
    },
    setPerPage(perPage) {
      setListState({ page: 1, perPage });
    },
    setSearchQuery(query) {
      setListState((state) => ({ ...state, page: 1, search: query }));
    },
    sortToString,
    sortFromString,
  };

  const utils = {
    sortToString,
    sortFromString,
  };

  return [{ ...props, ...context }, setListState, utils];
};

export const useListContext = (props) => {
  if (props.withRouter) {
    return useRouterListContext(props);
  }
  return useStateListContext(props);
};
